import React, { useEffect, useRef } from "react";
import { container } from "@/utils/mobx";
import "./s.app.css";
import { appStore } from "@/app_store";
import { t } from "@/i18n";
import { SignInSection } from "@/components/sections/SignInSection";
import { SignUpSection } from "@/components/sections/SignUpSection";
import { ActivityBar } from "@/components/ActivityBar";
import { CodeSection } from "@/components/sections/CodeSection";
import { DashboardSection } from "@/components/sections/DashboardSection";
import { ReviewSection } from "@/components/sections/ReviewSection";
import { DeploymentSection } from "@/components/sections/DeploymentSection";
import { AgentPane } from "@/components/AgentPane";
import { Split, SplitPane } from "@/components/toolkit/Split";
import { useSwipe } from "@/utils/swipe";
import { useAction } from "@/components/toolkit/InputProvider";
import { AppViewStore } from "./store";
import { LoadingOverlay } from "@/components/LoadingOverlay";

export const App = container((props: {
    store: AppViewStore
}) => {
    const store = props.store;

    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const update = () => {
            const z = parseFloat(document.documentElement.style.zoom) || 1;
            document.documentElement.style.setProperty("--app-height", `${vv.height / z}px`);
        };
        update();
        vv.addEventListener("resize", update);
        return () => vv.removeEventListener("resize", update);
    }, []);

    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => { e.preventDefault(); };
        document.addEventListener("contextmenu", handleContextMenu);
        return () => document.removeEventListener("contextmenu", handleContextMenu);
    }, []);

    useAction('app', 'go-to-code-and-search', () => { store.setSection("code"); appStore.state.openSearch(); }, [store]);
    useAction('app', 'toggle-agent', () => store.toggleAgent(), [store]);

    const SECTIONS: Array<"code" | "review" | "deploy"> = ["code", "review", "deploy"];
    const swipeNav = useRef(false);
    const swipeHandlers = useSwipe(
        () => {
            if (store.isAgentOpen) return;
            const idx = SECTIONS.indexOf(store.section);
            if (idx < SECTIONS.length - 1) { swipeNav.current = true; store.setSection(SECTIONS[idx + 1]); }
        },
        () => {
            if (store.isAgentOpen) { store.closeAgent(); return; }
            const idx = SECTIONS.indexOf(store.section);
            if (idx > 0) { swipeNav.current = true; store.setSection(SECTIONS[idx - 1]); }
        },
        () => {
            if (store.isAgentOpen && store.isMobile && store.isLandscape) store.closeAgent();
        }
    );

    if (appStore.isRefreshing) {
        return <div className="app__loading"><span className="app__loading_spinner" /></div>;
    }

    if (!appStore.account.isAuthenticated) {
        return (
            <div className="auth_section">
                <div className="auth_section__tabs">
                    <button
                        className={"auth_section__tab" + (store.authMode === "sign-in" ? " active" : "")}
                        onClick={() => store.setAuthMode("sign-in")}
                    >{t("app_sign_in_tab")}</button>
                    <button
                        className={"auth_section__tab" + (store.authMode === "sign-up" ? " active" : "")}
                        onClick={() => store.setAuthMode("sign-up")}
                    >{t("app_sign_up_tab")}</button>
                </div>
                {store.authMode === "sign-in" ? <SignInSection /> : <SignUpSection />}
            </div>
        );
    }

    if (!appStore.isInitialized) {
        return <div className="app__loading"><span className="app__loading_spinner" /></div>;
    }

    const hasProject = !!appStore.project.selectedProjectId;

    const activityBar = (
        <ActivityBar
            section={store.section}
            onSectionChange={(s: "code" | "review" | "deploy") => { swipeNav.current = false; store.setSection(s); }}
            isAgentOpen={store.isAgentOpen}
            onAgentToggle={() => store.toggleAgent()}
            name="activity_bar"
        />
    );

    if (!hasProject) {
        return (
            <div className="app">
                {activityBar}
                <main className="app__content">
                    <DashboardSection />
                    {appStore.project.isSelectingProject && <LoadingOverlay />}
                </main>
            </div>
        );
    }

    const activeIdx = SECTIONS.indexOf(store.section);
    const sections = (
        <div
            className={"app__sections" + (store.isMobile ? " app__sections--slide" : "")}
            data-animated={store.isMobile && swipeNav.current}
            style={store.isMobile ? { "--active-index": activeIdx } as React.CSSProperties : undefined}
        >
            <div className="app__section" data-active={store.section === "code"} style={store.isMobile ? { "--section-index": 0 } as React.CSSProperties : undefined}>
                <CodeSection name="code" />
            </div>
            <div className="app__section" data-active={store.section === "review"} style={store.isMobile ? { "--section-index": 1 } as React.CSSProperties : undefined}>
                <ReviewSection name="review" />
            </div>
            <div className="app__section" data-active={store.section === "deploy"} style={store.isMobile ? { "--section-index": 2 } as React.CSSProperties : undefined}>
                <DeploymentSection name="deploy" />
            </div>
        </div>
    );

    const agentPane = <AgentPane name="agent" />;

    if (store.isMobile) {
        return (
            <div className="app">
                {activityBar}
                <main className="app__content" {...swipeHandlers}>
                    {sections}
                    <div className="app__mobile_agent" data-visible={store.isAgentOpen}>
                        {agentPane}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="app" data-agent-open={store.isAgentOpen}>
            {activityBar}
            <main className="app__content">
                <Split
                    className="app__agent_split"
                    split="vertical"
                    sizes={store.isAgentOpen ? store.agentSizes : ['100%', 0]}
                    onChange={store.isAgentOpen ? (sizes: number[]) => store.setAgentSizes(sizes) : () => {}}
                >
                    <SplitPane minSize={100}>{sections}</SplitPane>
                    <SplitPane minSize={store.isAgentOpen ? 80 : 0}>{agentPane}</SplitPane>
                </Split>
            </main>
        </div>
    );
}, AppViewStore);
